import { ValueOf } from 'shared/types/generic'

export interface CustomerAddress {
  id: string
  isDefault?: boolean
  city: string
  name: string
  firstName: string
  lastName: string
  postalCode: string
  street: string
  houseNumber: string
  phoneNumber: string
  staircaseNumber?: string | null
  clientDeliveryNotes?: string | null
  floor?: string | number | null
  flatNumber?: string | null
  type: string
  customerId: string
  iconType?: string | null
  iconColor?: string | null
  longitude?: number | null
  latitude?: number | null
}

export interface CustomerInvoiceAddress {
  data: {
    [x: string]: string | null | undefined
  }
  isDefault: boolean
  city: string
  name: string
  postalCode: string
  street: string
  houseNumber: string
  floor?: string
  flatNumber?: string
  type: string
  companyName: string
  customerId: string
  id: string
  nip: string
  firstName: string
  lastName: string
  updatedAt: Date
  createdAt: Date
  iconType?: string
  iconColor?: string
  invoiceType: InvoiceType
}

export interface CustomerDeliveryAddressFormValues {
  id: string
  name: string
  firstName: string
  lastName: string
  iconColor?: string | null
  iconType?: string | null
  phoneNumber: string
  postalCode: string
  city: string
  street: string
  houseNumber: string
  staircaseNumber?: string | null
  flatNumber?: string | null
  floor?: string | number | null
  clientDeliveryNotes?: string | null
  isDefault?: boolean | null
  latitude?: number | null
  longitude?: number | null
}

export interface CustomerDeliveryAddressPayload {
  id: string
  city: string
  name: string
  firstName: string
  lastName: string
  postalCode: string
  phoneNumber: string
  street: string
  houseNumber: string
  flatNumber?: string | null
  floor?: string | number | null
  staircaseNumber?: string | null
  buildingName?: string | null
  clientDeliveryNotes?: string | null
  isDefault?: boolean | null
  type: CustomerAddressType
  iconType?: string | null
  iconColor?: string | null
  longitude?: number | null
  latitude?: number | null
}

export interface CustomerInvoiceAddressFormValues {
  id: string
  name: string
  invoiceType: InvoiceType
  iconType?: string
  iconColor?: string
  companyName: string
  taxId: string
  postalCode: string
  firstName: string
  lastName: string
  city: string
  street: string
  houseNumber: string
  flatNumber?: string
  isDefault?: boolean
}

/**
 * ApiService will automatically translate camelCase properties to snake_case.
 * @interface
 */
export interface CustomerInvoiceAddressPayload {
  city: string
  name: string
  invoiceType: InvoiceType
  postalCode: string
  houseNumber: string
  flatNumber?: string
  floor?: number
  type: CustomerAddressType
  street: string
  nip: string
  companyName: string
  firstName: string
  lastName: string
  isDefault?: boolean
  id?: string
  iconType?: string
  iconColor?: string
}

export const CustomerAddressType = {
  invoice: 'invoice',
  delivery: 'delivery'
} as const

export type CustomerAddressType = ValueOf<typeof CustomerAddressType>

export const InvoiceType = {
  company: 'company',
  personal: 'personal'
} as const

export type InvoiceType = ValueOf<typeof InvoiceType>

export const ResponseStatus = {
  initial: 'initial',
  pending: 'pending',
  fulfilled: 'fulfilled',
  rejected: 'rejected'
} as const

export type ResponseStatus = ValueOf<typeof ResponseStatus>

export type TSelectOption = {
  label: string
  value: string
  labelRaw?: string
  meta?: {
    activeColor?: string
  }
}

export const colorsData: TSelectOption[] = [
  { label: '', value: '#ffa31a' },
  { label: '', value: '#ffff00' },
  { label: '', value: '#2eb82e' },
  { label: '', value: '#3385ff' },
  { label: '', value: '#d279d2' }
]

export const iconsData: TSelectOption[] = [
  { label: '', value: 'home' },
  { label: '', value: 'company' }
]

type ComponentName = {
  text: string
  languageCode?: string
}

type PostalAddress = {
  regionCode: string
  languageCode: string
  postalCode: string
  locality: string
  addressLines: string[]
}

export type AddressComponent = {
  componentName: ComponentName
  componentType: string
  confirmationLevel: string
  inferred?: boolean
}

type Address = {
  formattedAddress: string
  postalAddress: PostalAddress
  addressComponents: AddressComponent[]
}

type Location = {
  latitude: number
  longitude: number
}

type PlusCode = {
  globalCode: string
}

type Bounds = {
  low: Location
  high: Location
}

type Geocode = {
  location: Location
  plusCode: PlusCode
  bounds: Bounds
  placeId: string
  placeTypes: string[]
}

type Metadata = {
  business: boolean
  residential: boolean
}

type Verdict = {
  inputGranularity: string
  validationGranularity: string
  geocodeGranularity: string
  addressComplete: boolean
  hasInferredComponents: boolean
}

type Result = {
  verdict: Verdict
  address: Address
  geocode: Geocode
  metadata: Metadata
}

export interface AddressValidationResponse {
  result: Result
  responseId: string
}
